import { IWatrRequest, IWatrRequestWeeks } from '@nkr/models/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { WEEKLY_CAP, calculateAdjustedBaseAmount, calculateAdjustedWeeklyReimbursement, getDefaultWeeklyWageBase } from './week-by-week-breakdown';

export const isWeekEligibleForWageBreakdown = (status: WageAndTravelReimbursementRequestStatus): boolean =>
    status > WageAndTravelReimbursementRequestStatus.PendingApproval
    && status < WageAndTravelReimbursementRequestStatus.CenterDeclined;

export interface WageReimbursementWeekRow {
    weekRequested: number;
    baseWeeklyAmount: number;
    otherAmount: number;
    adjustedAmount: number;
}

export interface WageReimbursementSuggestedResult {
    adjustedBaseAmount: number;
    rows: WageReimbursementWeekRow[];
    suggestedTotal: number;
    usedSingleWeekFallback: boolean;
    noEligibleWeeks: boolean;
    allWeeksReimbursed: boolean;
}

export function filterEligibleWageWeeks(weeks: IWatrRequestWeeks[] | null | undefined): IWatrRequestWeeks[] {
    if (!weeks || weeks.length === 0) {
        return [];
    }
    return weeks
        .filter(w => isWeekEligibleForWageBreakdown(w.status))
        .sort((a, b) => a.weekRequested - b.weekRequested);
}

function computeAdjustedBaseFromRequest(request: IWatrRequest): number {
    const rawBaseAmount = getDefaultWeeklyWageBase(request);
    const cappedBaseAmount = Math.min(rawBaseAmount, WEEKLY_CAP);
    const roundedBaseAmount = Math.round(cappedBaseAmount * 100) / 100;
    const incomePercentCovered = request.incomePercentCovered || 0;
    return calculateAdjustedBaseAmount(roundedBaseAmount, incomePercentCovered, WEEKLY_CAP);
}

export function getWageReimbursementSuggested(request: IWatrRequest): WageReimbursementSuggestedResult {
    const adjustedBaseAmount = computeAdjustedBaseFromRequest(request);
    const requestWeeks = request.requestWeeks || [];
    const eligibleWeeks = filterEligibleWageWeeks(requestWeeks);

    if (eligibleWeeks.length > 0) {
        const rows: WageReimbursementWeekRow[] = eligibleWeeks.map(week => {
            const otherAmount = week.otherAmount || 0;
            const adjustedAmount = calculateAdjustedWeeklyReimbursement(adjustedBaseAmount, otherAmount, WEEKLY_CAP);
            return {
                weekRequested: week.weekRequested,
                baseWeeklyAmount: adjustedBaseAmount,
                otherAmount,
                adjustedAmount
            };
        });
        const suggestedTotal = rows.reduce((sum, r) => sum + r.adjustedAmount, 0);
        return {
            adjustedBaseAmount,
            rows,
            suggestedTotal,
            usedSingleWeekFallback: false,
            noEligibleWeeks: false,
            allWeeksReimbursed: false
        };
    }

    if (requestWeeks.length === 0) {
        const suggestedTotal = adjustedBaseAmount;
        return {
            adjustedBaseAmount,
            rows: [],
            suggestedTotal,
            usedSingleWeekFallback: true,
            noEligibleWeeks: false,
            allWeeksReimbursed: false
        };
    }

    const allReimbursed = requestWeeks.length > 0
        && requestWeeks.every(w => w.status === WageAndTravelReimbursementRequestStatus.Reimbursed);

    return {
        adjustedBaseAmount,
        rows: [],
        suggestedTotal: 0,
        usedSingleWeekFallback: false,
        noEligibleWeeks: true,
        allWeeksReimbursed: allReimbursed
    };
}

const OTHER_EPS = 0.005;

export interface IOtherAmountConsistencyHint {
    weeksBreakingPriorZeroPattern: number[];
    anyEarlierWeekHadOtherAmount: boolean;
}

export function getOtherAmountConsistencyHint(request: IWatrRequest): IOtherAmountConsistencyHint {
    const eligible = filterEligibleWageWeeks(request.requestWeeks);
    if (eligible.length < 2) {
        return { weeksBreakingPriorZeroPattern: [], anyEarlierWeekHadOtherAmount: false };
    }

    const anyEarlierWeekHadOtherAmount = eligible.some((_, i) =>
        i > 0 && eligible.slice(0, i).some(p => (p.otherAmount || 0) > OTHER_EPS));

    const breaking: number[] = [];
    for (let i = 0; i < eligible.length; i++) {
        const other = eligible[i].otherAmount || 0;
        if (other <= OTHER_EPS) {
            continue;
        }
        const priors = eligible.slice(0, i);
        if (priors.length === 0) {
            continue;
        }
        if (priors.every(p => (p.otherAmount || 0) <= OTHER_EPS)) {
            breaking.push(eligible[i].weekRequested);
        }
    }

    return {
        weeksBreakingPriorZeroPattern: breaking,
        anyEarlierWeekHadOtherAmount
    };
}

export interface IPriorReimbursedWeeksOtherSummary {
    reimbursedWeekCount: number;
    reimbursedWeekNumbers: number[];
    reimbursedWithOtherCount: number;
    weekNumbersWithOther: number[];
}

export function getPriorReimbursedWeeksOtherSummary(request: IWatrRequest): IPriorReimbursedWeeksOtherSummary {
    const weeks = request.requestWeeks || [];
    const reimbursed = weeks
        .filter(w => w.status === WageAndTravelReimbursementRequestStatus.Reimbursed)
        .sort((a, b) => a.weekRequested - b.weekRequested);
    const withOther = reimbursed.filter(w => (w.otherAmount || 0) > OTHER_EPS);
    return {
        reimbursedWeekCount: reimbursed.length,
        reimbursedWeekNumbers: reimbursed.map(w => w.weekRequested),
        reimbursedWithOtherCount: withOther.length,
        weekNumbersWithOther: withOther.map(w => w.weekRequested)
    };
}
